'use client';

import { TallerButtonLink } from "@/components/taller/button";
import { SectionHeading } from "@/components/taller/section-heading";
import { useTranslations } from "@/i18n/client";

export function AcercaDeContent() {
  const { t, isLoading } = useTranslations();
  const ad = t('acercaDe');

  if (isLoading) return null;

  const pillars = ad.pillars?.items ?? [
    { title: 'Sin riesgo', body: 'Sin pago inicial. Pagas solo cuando el resultado te encanta.' },
    { title: 'Resultados reales', body: 'Webs orientadas a captar clientes, no solo a quedar bonitas.' },
    { title: 'Equipo cercano', body: 'Trato directo, sin intermediarios, con personas reales detrás.' },
  ];

  const stats = ad.stats ?? [
    { value: '+50', label: 'proyectos entregados' },
    { value: '72h', label: 'primera maqueta' },
    { value: '100%', label: 'sin pago inicial' },
    { value: 'Madrid', label: 'base de operaciones' },
  ];

  return (
    <>
      {/* HERO */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-[72px] pb-16">
        <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)]">
          {ad.hero?.overline || 'Quiénes somos'}
        </div>
        <h1 className="taller-page-title mt-7">
          {ad.hero?.h1Part1 || 'Tecnología al '}
          <em className="italic text-[color:var(--color-accent)]">{ad.hero?.h1Accent || 'servicio'}</em>
          <br />
          {ad.hero?.h1Part2 || 'de tu negocio.'}
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mt-14 items-end">
          <p className="font-sans text-[19px] leading-[1.55] text-[color:var(--color-mute)] m-0 max-w-[540px]">
            {ad.hero?.subtitle || 'CODIAJ es un equipo de expertos en soluciones digitales en Madrid.'}
          </p>
          <div className="flex gap-3 lg:justify-end flex-wrap">
            <TallerButtonLink variant="primary" href="/contacto">
              {ad.hero?.ctaPrimary || 'Hablemos de tu proyecto →'}
            </TallerButtonLink>
            <TallerButtonLink variant="ghost" href="/servicios">
              {ad.hero?.ctaSecondary || 'Ver servicios'}
            </TallerButtonLink>
          </div>
        </div>
      </section>

      <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14">
        <div className="border-t border-[color:var(--color-border)]" />
      </div>

      {/* VALUE PILLARS */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-24">
        <SectionHeading
          overline={ad.pillars?.overline || 'Nuestra forma de trabajar'}
          heading={
            <>
              {ad.pillars?.heading || 'Tres principios que '}
              <em className="italic text-[color:var(--color-accent)]">
                {ad.pillars?.headingAccent || 'no negociamos'}
              </em>.
            </>
          }
          size="lg"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-4">
          {pillars.map((p, i) => (
            <div key={p.title} className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-md p-8">

              <h2 className="font-sans font-[350] text-[28px] leading-[1.1] tracking-[-0.015em] text-[color:var(--color-ink)] m-0 mb-4">{p.title}</h2>
              <p className="font-sans text-[15px] leading-[1.65] text-[color:var(--color-mute)] m-0">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="bg-[color:var(--color-bg)] py-24">
        <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-[80px_1fr] gap-6">
            <div />
            <div className="max-w-[720px]">
              <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)]">{ad.historia?.overline || 'Nuestra historia'}</div>
              <h2 className="font-sans font-[350] text-[34px] sm:text-[44px] md:text-[56px] leading-[1.02] tracking-[-0.02em] text-[color:var(--color-ink)] mt-3 mb-8">
                {ad.historia?.heading || 'Nacimos en Madrid. Trabajamos para toda España.'}
              </h2>
              <div className="space-y-5 font-sans text-[17px] leading-[1.75] text-[color:var(--color-mute)]">
                <p>{ad.historia?.p1}</p>
                <p>{ad.historia?.p2}</p>
                <p>{ad.historia?.p3}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[color:var(--color-border)] pt-14">
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col gap-2">
              <div className="font-sans font-[350] text-[40px] sm:text-[52px] leading-[1] tracking-[-0.03em] text-[color:var(--color-accent)]">{stat.value}</div>
              <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-[color:var(--color-mute)]">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[color:var(--color-bg)] py-24">
        <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14">
          <div className="border-t border-[color:var(--color-border)] pt-14 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-12 items-end">
            <h2 className="font-sans font-[350] text-[40px] sm:text-[52px] md:text-[72px] lg:text-[88px] leading-[0.95] tracking-[-0.03em] m-0 text-[color:var(--color-ink)]">
              {ad.cta?.heading || 'Hablemos de '}
              <em className="italic text-[color:var(--color-accent)]">{ad.cta?.headingAccent || 'tu proyecto'}</em>.
            </h2>
            <div>
              <p className="font-sans text-[17px] leading-[1.55] text-[color:var(--color-mute)] mt-0 mb-6">
                {ad.cta?.subtitle || 'Sin compromisos. Una maqueta en 72 horas y tú decides si seguimos.'}
              </p>
              <TallerButtonLink variant="primary" href="/contacto" className="!py-[18px] !px-7 !tracking-[0.2em]">
                {ad.cta?.button || 'Empezar proyecto →'}
              </TallerButtonLink>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
